import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import debugFactory from 'debug';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import React from 'react';

import { LocationLostModal, SelectedChoiceLostModal } from '~src/category/category-lost-modal';
import {
  checkCategoryLost,
  checkChoiceLost,
  compareNewAndOldSessionsById,
} from '~src/category/check-category-lost';
import { globalConfig } from '~src/context/config';
import { apiInstance, useGlobalState, useGlobalStateUpdate } from '~src/global-state';

import { FirestoreToken, StateResponse } from '../../api/api';

const debug = debugFactory('ingrid:firestore');

export function useFirestoreListener(callback: (state: StateResponse) => void) {
  const globalState = useGlobalState();

  const { data } = useQuery<FirestoreToken, AxiosError>({
    queryKey: ['firestore'],
    queryFn: async () => {
      const { data } = await apiInstance.v4.tokenGet(globalState.checkout_session_id);
      return data;
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const token = data?.token;

  React.useEffect(() => {
    if (!token) {
      return;
    }

    debug('Initialize firestore');

    try {
      initializeApp(globalConfig.firebaseConfig);
      signInWithCustomToken(getAuth(), token).then(() => {
        debug('Sign-in completed');

        const db = getFirestore();
        const docRef = doc(db, globalConfig.firebaseCollection, globalState.checkout_session_id);

        /**
         * FIXME: onSnapshot will not be called if callback changes.
         */
        return onSnapshot(docRef, doc => {
          const rawData = doc.data();
          if (!rawData) {
            return;
          }
          const newData = rawData.session;
          debug(`Received message: ${newData}`);

          const newState = JSON.parse(newData);

          callback(newState as StateResponse);
        });
      });
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
    }
  }, [token]);
}

const FirestoreListener: React.FC = () => {
  const updateGlobalState = useGlobalStateUpdate();
  const queryClient = useQueryClient();
  const globalState = useGlobalState();

  const [categoryLost, setCategoryLost] = React.useState(false);
  const [locationLost, setLocationLost] = React.useState(false);

  useFirestoreListener((newState: StateResponse) => {
    const oldState = queryClient.getQueryData(['state']) as StateResponse;

    if (newState.sessions[0].flags.disable_choice_lost_modal_if_similar_selected) {
      if (compareNewAndOldSessionsById(newState, oldState)) {
        setCategoryLost(true);
      }
    } else {
      if (checkCategoryLost(newState, oldState)) {
        setCategoryLost(true);
      } else if (checkChoiceLost(newState, oldState)) {
        setLocationLost(true);
      }
    }

    updateGlobalState(newState);
  });

  if (categoryLost) {
    return <SelectedChoiceLostModal onClose={() => setCategoryLost(false)} />;
  }

  if (globalState.sessions[0].flags.show_location_unavailable_modal_message && locationLost) {
    return (
      <LocationLostModal
        onClose={() => {
          setLocationLost(false);
        }}
      />
    );
  }

  return null;
};

export const FirestoreListenerProxy: React.FC = () => {
  const globalState = useGlobalState();
  // TODO: move the config to global state.
  const session = globalState.sessions[0];

  if (session.flags.send_state_events) {
    return <FirestoreListener />;
  }

  return null;
};
